import { useEffect, useState } from "react";
import { ArrowUpRight, Play, X } from "lucide-react";

const SERVICES = [
  {
    n: "01",
    title: "Photography",
    desc: "Interiors, exteriors, and editorial-grade imagery captured with intent. Clean, complete, and ready for MLS or the cover of a magazine.",
    imgs: [
      "/gallery/photography/01.jpg",
      "/gallery/photography/02.jpg",
      "/gallery/photography/03.jpg",
      "/gallery/photography/04.jpg",
    ],
    pricing: [
      { label: "Interior Photos", price: "$195" },
      { label: "Interior / Exterior Photos", price: "$235" },
      { label: "Interior / Exterior / Aerial Photos", price: "$295" },
      { label: "Twilight Photo", price: "$300" },
      { label: "Editorial Photos — add-on", price: "$95" },
      { label: "Virtual Twilight — add-on", price: "$30" },
      { label: "Virtual Staging — add-on", price: "$35" },
    ],
  },
  {
    n: "02",
    title: "Video",
    desc: "Cinematic property tours, vertical reels, and agent soundbites — built for social-first audiences and high-end listings.",
    layout: "split",
    imgs: [
      {
        video: "https://customer-assets.emergentagent.com/job_property-lens-10/artifacts/9s18ijn3_Timeline%205.mp4",
        poster: "/gallery/video/01.jpg",
        label: "Cinematic Tour",
        aspect: "landscape",
        fullVideo: "vimeo:1208909813",
      },
      {
        video: "https://customer-assets.emergentagent.com/job_property-lens-10/artifacts/k3uem8gf_Timeline%204.mp4",
        poster: "/gallery/social/01.jpg",
        label: "Vertical Reel",
        aspect: "portrait",
        fullVideo: "vimeo:1208907735",
      },
    ],
    pricing: [
      { label: "Property Video Tour — 60–90 sec, H or V", price: "$405" },
      { label: "Agent Brand Reel — vertical, 60 sec", price: "$525" },
      { label: "One-Take Reel", price: "$195" },
      { label: "Cinematic Video", price: "$975" },
      { label: "Twilight Photo & Video", price: "$375" },
      { label: "Vertical Add-On", price: "$85" },
      { label: "Video Carousel — add-on", price: "$85" },
    ],
  },
  {
    n: "03",
    title: "Drone & Aerial",
    desc: "FAA-certified pilots, considered angles. Show context, scale, and surroundings — never just an obligatory flyover.",
    imgs: [
      "/gallery/drone/01.jpg",
      "/gallery/drone/02.jpg",
      "/gallery/drone/03.jpg",
      "/gallery/drone/04.jpg",
    ],
    pricing: [
      { label: "Aerial Photos", price: "$150" },
      { label: "Interior / Exterior / Aerial Photos", price: "$295" },
    ],
  },
  {
    n: "04",
    title: "3D Tours",
    desc: "Immersive virtual walkthroughs that let buyers explore a home from anywhere — clean, fast, embeddable.",
    imgs: [
      { video: "https://customer-assets.emergentagent.com/job_property-lens-10/artifacts/rdvu02tu_SHORT-INTRO-480p-MP4.mp4", poster: "/gallery/matterport/01.jpg", label: "Matterport" },
      { src: "/gallery/matterport/zillow-01.jpg", label: "Zillow 3D" },
    ],
    pricing: [
      { label: "Matterport & Zillow 3D", price: "$225" },
    ],
  },
  {
    n: "05",
    title: "Floor Plans",
    desc: "Simple, beautiful schematic floor plans rendered to MLS standards and finished with restraint.",
    fit: "contain",
    bg: "#f6f6f4",
    imgs: [
      "/gallery/floorplans/01.jpg",
    ],
    pricing: [
      { label: "Simple Floor Plan", price: "$95" },
    ],
  },
];

function gridClass(s) {
  if (s.layout === "split") return "grid-cols-1 md:grid-cols-[1.7fr_1fr] items-center";
  if (s.imgs.length <= 2) return "grid-cols-1 md:grid-cols-2";
  return "grid-cols-2 md:grid-cols-4";
}

function aspectClass(s, item) {
  if (s.layout === "split") {
    return item.aspect === "portrait" ? "aspect-[9/16]" : "aspect-[16/9]";
  }
  if (s.imgs.length <= 2) return "aspect-[4/3]";
  return "aspect-[4/5]";
}

function itemKey(item) {
  if (typeof item === "string") return item;
  return item.video || item.src;
}

const MediaTile = ({ service, item, serviceIndex, tileIndex, open, onOpenLightbox }) => {
  const isVideo = typeof item === "object" && item.video;
  const isLabeled = typeof item === "object" && (item.src || item.video);
  const src = isVideo ? item.video : (isLabeled ? item.src : item);
  const poster = isVideo ? item.poster : undefined;
  const caption = isLabeled
    ? item.label
    : `${service.title} · ${String(tileIndex + 1).padStart(2, "0")}`;

  const handleClick = (e) => {
    if (!item?.fullVideo) return;
    e.stopPropagation();
    onOpenLightbox({
      src: item.fullVideo,
      aspect: item.aspect === "portrait" ? "portrait" : "landscape",
    });
  };

  return (
    <figure
      key={itemKey(item)}
      data-testid={`service-${serviceIndex + 1}-img-${tileIndex + 1}`}
      onClick={handleClick}
      className={`media-frame relative overflow-hidden ${aspectClass(service, item)} ${item?.fullVideo ? "cursor-pointer group/tile" : ""}`}
      style={{
        background: service.bg || "#f6f6f4",
        transitionDelay: open ? `${tileIndex * 60}ms, ${tileIndex * 60}ms` : "0ms, 0ms",
        transform: open ? "translateY(0)" : "translateY(12px)",
        opacity: open ? 1 : 0,
        transitionProperty: "transform, opacity",
        transitionDuration: "600ms, 600ms",
        transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1), ease",
      }}
    >
      {isVideo ? (
        <video
          src={open ? src : undefined}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={src}
          alt={caption}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${service.fit === "contain" ? "object-contain p-2" : "object-cover"}`}
        />
      )}
      <span
        className="absolute left-3 bottom-3 overline"
        style={{ color: "rgba(255,255,255,0.95)", mixBlendMode: "difference" }}
      >
        {caption}
      </span>
      {item?.fullVideo && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 bg-black/0 group-hover/tile:bg-black/30 transition-colors duration-500 pointer-events-none"
          />
          <div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/tile:opacity-100 scale-90 group-hover/tile:scale-100 transition-all duration-500 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.55)]">
              <Play size={22} className="text-black translate-x-[1px]" fill="currentColor" />
            </div>
          </div>
          <span className="absolute right-3 bottom-3 overline text-white/90 mix-blend-difference">
            Watch full ↗
          </span>
        </>
      )}
    </figure>
  );
};

const PriceList = ({ serviceIndex, items }) => (
  <div
    data-testid={`service-${serviceIndex + 1}-pricing`}
    className="mt-2 mb-10 md:mb-14 border-t border-black/10 pt-6"
  >
    <span className="overline block mb-5">A la carte</span>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-3 max-w-5xl">
      {items.map((it) => (
        <div key={it.label} className="flex items-baseline gap-3">
          <span className="text-[14px] text-[#000000]">{it.label}</span>
          <span aria-hidden className="flex-1 border-b border-dotted border-black/25 translate-y-[-3px]" />
          <span className="font-serif-display text-xl md:text-2xl whitespace-nowrap">{it.price}</span>
        </div>
      ))}
    </div>
    <div className="mt-6 inline-flex items-center gap-3 bg-[#f6f6f4] border-l-2 border-[#c9a878] px-4 py-3">
      <span className="text-[13px] font-medium tracking-wide text-[#000000]">
        Starting rates — final pricing based on square footage
      </span>
    </div>
  </div>
);

const ServiceRow = ({ service, index, open, dimmed, onToggle, onHover, onOpenLightbox }) => (
  <li
    data-testid={`service-${service.title.toLowerCase().replace(/\s|&/g, "-")}`}
    onMouseEnter={onHover}
    className="border-b border-black/10"
  >
    <button
      type="button"
      onClick={onToggle}
      data-testid={`service-toggle-${index + 1}`}
      className={`w-full text-left grid grid-cols-12 gap-4 md:gap-6 py-7 md:py-9 cursor-pointer transition-opacity duration-300 ${
        dimmed ? "opacity-40" : "opacity-100"
      }`}
      aria-expanded={open}
    >
      <div className="col-span-2 md:col-span-1 overline pt-2 md:pt-3">
        {service.n}
      </div>
      <div className="col-span-10 md:col-span-4">
        <h3 className={`font-serif-display text-2xl md:text-4xl tracking-tight transition-all duration-500 ${open ? "translate-x-2 text-[#c9a878]" : ""}`}>
          {service.title}
        </h3>
      </div>
      <div className="col-span-12 md:col-span-5 text-[15px] leading-[1.7] text-[#3a3a3a]">
        {service.desc}
      </div>
      <div className="col-span-12 md:col-span-2 flex md:justify-end items-start md:items-center">
        <ArrowUpRight
          size={20}
          className={`transition-transform duration-500 ${open ? "translate-x-1 -translate-y-1 text-[#c9a878] rotate-45" : ""}`}
        />
      </div>
    </button>

    <div
      className="overflow-hidden transition-[grid-template-rows] duration-700 ease-out grid"
      style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      aria-hidden={!open}
    >
      <div className="min-h-0">
        <div className={`pb-8 md:pb-10 grid gap-3 md:gap-4 ${gridClass(service)}`}>
          {service.imgs.map((item, j) => (
            <MediaTile
              key={itemKey(item)}
              service={service}
              item={item}
              serviceIndex={index}
              tileIndex={j}
              open={open}
              onOpenLightbox={onOpenLightbox}
            />
          ))}
        </div>
        {service.pricing && <PriceList serviceIndex={index} items={service.pricing} />}
      </div>
    </div>
  </li>
);

const VimeoPlayer = ({ id, aspect }) => {
  const isPortrait = aspect === "portrait";
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="relative bg-black shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
      style={{
        width: isPortrait ? "min(calc(92svh * 0.5625), 92vw)" : "min(92vw, calc(92svh * 1.7778))",
        aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
      }}
    >
      <iframe
        src={`https://player.vimeo.com/video/${id}?autoplay=1&title=0&byline=0&portrait=0&dnt=1&playsinline=1`}
        title="structure media video"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
      />
    </div>
  );
};

export const VideoLightbox = ({ lightbox, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const isVimeo = lightbox.src.startsWith("vimeo:");

  return (
    <div
      data-testid="video-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
    >
      <button
        type="button"
        aria-label="Close video"
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-5 right-5 md:top-8 md:right-8 w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors z-10"
      >
        <X size={20} />
      </button>
      {isVimeo ? (
        <VimeoPlayer id={lightbox.src.replace("vimeo:", "")} aspect={lightbox.aspect} />
      ) : (
        <video
          src={lightbox.src}
          controls
          autoPlay
          playsInline
          className="max-w-[100%] max-h-[92svh] w-auto h-auto shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
};

export default function Services() {
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const toggle = (i) => setActive((cur) => (cur === i ? null : i));

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 border-t border-black/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-3">
            <span className="overline">02 — Services</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] max-w-4xl">
              <span className="italic-display text-[#c9a878]">Listings</span>{" "}
              content. Built clean. Ready to perform.
            </h2>
          </div>
        </div>

        <ul
          className="border-t border-black/10"
          data-testid="services-list"
          onMouseLeave={() => setActive(null)}
        >
          {SERVICES.map((s, i) => (
            <ServiceRow
              key={s.n}
              service={s}
              index={i}
              open={active === i}
              dimmed={active !== null && active !== i}
              onToggle={() => toggle(i)}
              onHover={() => setActive(i)}
              onOpenLightbox={setLightbox}
            />
          ))}
        </ul>
      </div>

      {lightbox && (
        <VideoLightbox lightbox={lightbox} onClose={() => setLightbox(null)} />
      )}
    </section>
  );
}
